import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Paper,
  InputAdornment,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  styled,
  useTheme,
  useMediaQuery,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Person,
  Lock
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import logogpImage from '../assets/logogp.png';

const StyledContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  minWidth: '100vw',
  margin: 0,
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#1E1E1E',
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
    alignItems: 'flex-start',
    paddingTop: theme.spacing(4),
  },
  '&::before': {
    content: '""',
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '200vw',
    height: '200vh',
    background: 'radial-gradient(circle at center, #2E2E2E 0%, #1E1E1E 50%)',
    opacity: 0.6,
    animation: 'pulse 8s ease-in-out infinite',
  },
  '@keyframes pulse': {
    '0%': {
      transform: 'translate(-50%, -50%) scale(1)',
    },
    '50%': {
      transform: 'translate(-50%, -50%) scale(1.2)',
    },
    '100%': {
      transform: 'translate(-50%, -50%) scale(1)',
    },
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(4),
  background: 'rgba(18, 18, 18, 0.8)',
  backdropFilter: 'blur(10px)',
  borderRadius: '20px',
  boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  width: '100%',
  maxWidth: '400px',
  overflow: 'hidden',
  animation: 'fadeIn 0.5s ease-out',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
    borderRadius: '16px',
    maxWidth: '100%',
  },
  '@keyframes fadeIn': {
    from: {
      opacity: 0,
      transform: 'translateY(-20px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  marginBottom: '2rem',
  [theme.breakpoints.down('sm')]: {
    marginBottom: '1.5rem',
  },
  '& img': {
    width: '120px',
    height: 'auto',
    filter: 'brightness(0) saturate(100%) invert(100%) sepia(100%) saturate(0%) hue-rotate(288deg) brightness(102%) contrast(102%)',
    animation: 'logoFloat 6s ease-in-out infinite',
    [theme.breakpoints.down('sm')]: {
      width: '100px',
    },
  },
  '@keyframes logoFloat': {
    '0%, 100%': {
      transform: 'translateY(0) rotate(0deg)',
    },
    '25%': {
      transform: 'translateY(-15px) rotate(5deg)',
    },
    '75%': {
      transform: 'translateY(15px) rotate(-5deg)',
    },
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '& .MuiOutlinedInput-root': {
    transition: 'all 0.3s ease',
    background: 'rgba(255, 255, 255, 0.05)',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.08)',
    },
    '&.Mui-focused': {
      background: 'rgba(255, 255, 255, 0.1)',
      transform: 'scale(1.02)',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  '& .MuiOutlinedInput-input': {
    color: 'rgba(255, 255, 255, 0.9)',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255, 255, 255, 0.2)',
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255, 255, 255, 0.3)',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#00FF00',
  },
}));

const StyledButton = styled(Button)({
  marginTop: '16px',
  padding: '12px',
  backgroundColor: '#00FF00',
  color: '#000000',
  fontWeight: 600,
  borderRadius: '12px',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: '#00CC00',
    transform: 'translateY(-2px)',
    boxShadow: '0 5px 15px rgba(0, 255, 0, 0.3)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  '&.Mui-disabled': {
    backgroundColor: 'rgba(0, 255, 0, 0.5)',
  },
});

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [usuario, setUsuario] = useState('');
  const [senha, setSenha] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [lembrarMe, setLembrarMe] = useState(false);

  // Carregar credenciais salvas ao iniciar componente
  React.useEffect(() => {
    const usuarioSalvo = localStorage.getItem('lembrar_usuario');
    const senhaSalva = localStorage.getItem('lembrar_senha');
    if (usuarioSalvo && senhaSalva) {
      setUsuario(usuarioSalvo);
      setSenha(senhaSalva);
      setLembrarMe(true);
    }
  }, []);

  const handleUsuarioChange = (e) => {
    setUsuario(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSnackbar({ ...snackbar, open: false });

    try {
      if (!usuario) {
        setSnackbar({
          open: true,
          message: 'Usuário é obrigatório',
          severity: 'error',
        });
        setLoading(false);
        return;
      }

      if (!senha) {
        setSnackbar({
          open: true,
          message: 'Senha é obrigatória',
          severity: 'error',
        });
        setLoading(false);
        return;
      }

      const response = await authService.login(usuario, senha);

      // Garantir que primeiro_acesso seja boolean
      const isPrimeiroAcesso = response.primeiro_acesso === true || response.primeiro_acesso === 'true';

      // Salvar dados de autenticação
      await login(
        {
          access_token: response.access_token,
          refresh_token: response.refresh_token
        },
        response.usuario,
        isPrimeiroAcesso
      );

      // Salvar ou remover credenciais baseadas no checkbox lembrar-me
      if (lembrarMe) {
        localStorage.setItem('lembrar_usuario', usuario);
        localStorage.setItem('lembrar_senha', senha);
      } else {
        localStorage.removeItem('lembrar_usuario');
        localStorage.removeItem('lembrar_senha');
      }

      setSnackbar({
        open: true,
        message: 'Login realizado com sucesso!',
        severity: 'success',
      });

      // Redirecionar baseado no primeiro acesso
      setTimeout(() => {
        if (isPrimeiroAcesso) {
          navigate('/trocar-senha');
        } else {
          navigate('/');
        }
      }, 1000);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || 'Erro ao fazer login. Verifique suas credenciais.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <StyledContainer>
      <StyledPaper elevation={3} id="login-container">
        <LogoContainer>
          <img 
            src={logogpImage} 
            alt="Logo GP" 
            style={{ 
              filter: 'brightness(0) saturate(100%) invert(100%) sepia(100%) saturate(0%) hue-rotate(288deg) brightness(102%) contrast(102%)'
            }} 
          />
        </LogoContainer>

        <Typography
          variant={isMobile ? 'h5' : 'h4'}
          sx={{
            mb: isMobile ? 3 : 4,
            fontWeight: 600,
            textAlign: 'center',
            color: '#00FF00',
            fontSize: isMobile ? '1.5rem' : undefined,
          }}
        >
          NEO Partnership
          <br />
          <Box component="span" sx={{ fontWeight: 100, fontSize: isMobile ? '0.7em' : '0.75em' }}>
            Núcleo de Eficiência Operacional
          </Box>
        </Typography>

        <Box component="form" onSubmit={handleSubmit}>
          <StyledTextField
            fullWidth
            label="Usuário"
            name="usuario"
            value={usuario}
            onChange={handleUsuarioChange}
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                </InputAdornment>
              ),
            }}
          />

          <StyledTextField
            fullWidth
            label="Senha"
            name="senha"
            type={showPassword ? 'text' : 'password'}
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={lembrarMe}
                onChange={(e) => setLembrarMe(e.target.checked)}
                sx={{ color: 'rgba(255, 255, 255, 0.7)', '&.Mui-checked': { color: '#00FF00' } }}
              />
            }
            label={<Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.875rem' }}>Lembrar-me</Typography>}
            sx={{ mt: 1, mb: 2 }}
          />

          <StyledButton
            type="submit"
            fullWidth
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} sx={{ color: '#000000' }} /> : 'Entrar'}
          </StyledButton>
        </Box>
      </StyledPaper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ 
          vertical: isMobile ? 'bottom' : 'top', 
          horizontal: isMobile ? 'center' : 'right' 
        }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </StyledContainer>
  );
};

export default Login;
